import { AnimatePresence, motion } from 'framer-motion';
import { LockKeyhole, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

export type PrivateContent = {
  mainLetter: string;
  birthdayWish: string;
  dinnerInvitation: string;
  travelInvitation: string;
};

type PrivateContentContextValue = {
  unlocked: boolean;
  content: PrivateContent | null;
  ensureUnlocked: () => Promise<boolean>;
};

const API_ROOT = '/private-api';

const PrivateContentContext =
  createContext<PrivateContentContextValue | null>(null);

function reloadPrivateImages() {
  const timestamp = Date.now();

  document
    .querySelectorAll<HTMLImageElement>(
      [
        'img[src*="hero-photo.jpg"]',
        'img[src*="memory-1.jpg"]',
        'img[src*="memory-2.jpg"]',
        'img[src*="memory-3.jpg"]',
        'img[src*="memory-4.jpg"]',
        'img[src*="memory-5.jpg"]',
        'img[src*="memory-6.jpg"]',
        'img[src*="og-birthday.jpg"]',
      ].join(','),
    )
    .forEach((image) => {
      const url = new URL(image.src, window.location.href);
      url.searchParams.set('private', String(timestamp));
      image.src = url.toString();
    });
}

export function PrivateContentProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [unlocked, setUnlocked] = useState(false);
  const [content, setContent] = useState<PrivateContent | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const unlockedRef = useRef(false);
  const pendingResolvers = useRef<Array<(result: boolean) => void>>([]);
  const pendingElement = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const applyUnlockedState = useCallback((next: boolean) => {
    unlockedRef.current = next;
    setUnlocked(next);
    document.body.classList.toggle('private-content-unlocked', next);
  }, []);

  const loadPrivateContent = useCallback(async () => {
    const response = await fetch(`${API_ROOT}/content.php`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Private content could not be loaded.');
    }

    const data = (await response.json()) as PrivateContent;
    setContent(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetch(`${API_ROOT}/status.php`, {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((response) => response.json())
      .then(async (status: { unlocked?: boolean }) => {
        if (cancelled || !status.unlocked) return;

        applyUnlockedState(true);
        await loadPrivateContent();
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [applyUnlockedState, loadPrivateContent]);

  useEffect(() => {
    if (!modalOpen) return;

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [modalOpen]);

  const resolvePending = useCallback((result: boolean) => {
    const resolvers = pendingResolvers.current.splice(0);
    resolvers.forEach((resolve) => resolve(result));
  }, []);

  const closeModal = useCallback(() => {
    if (submitting) return;

    setModalOpen(false);
    setPassword('');
    setError('');
    pendingElement.current = null;
    resolvePending(false);
  }, [resolvePending, submitting]);

  const ensureUnlocked = useCallback(() => {
    if (unlockedRef.current) {
      return Promise.resolve(true);
    }

    setError('');
    setModalOpen(true);

    return new Promise<boolean>((resolve) => {
      pendingResolvers.current.push(resolve);
    });
  }, []);

  const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!password.trim() || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_ROOT}/auth.php`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        setError('Incorrect password. Please try again.');
        return;
      }

      applyUnlockedState(true);
      await loadPrivateContent();
      reloadPrivateImages();

      const elementToReplay = pendingElement.current;

      setModalOpen(false);
      setPassword('');
      setError('');
      pendingElement.current = null;
      resolvePending(true);

      if (elementToReplay) {
        window.setTimeout(() => {
          elementToReplay.click();
        }, 160);
      }
    } catch {
      setError('Unable to verify the password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const privateSelectors = [
      '.hero-photo-frame',
      '.memory-card',
      '.photo-heart',
    ].join(',');

    const capturePrivateClick = (event: MouseEvent) => {
      if (unlockedRef.current) return;

      const target = event.target;

      if (!(target instanceof Element)) return;

      const privateElement = target.closest<HTMLElement>(privateSelectors);

      if (!privateElement) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      pendingElement.current = privateElement;
      void ensureUnlocked();
    };

    document.addEventListener('click', capturePrivateClick, true);

    return () => {
      document.removeEventListener('click', capturePrivateClick, true);
    };
  }, [ensureUnlocked]);

  const value = useMemo(
    () => ({
      unlocked,
      content,
      ensureUnlocked,
    }),
    [content, ensureUnlocked, unlocked],
  );

  return (
    <PrivateContentContext.Provider value={value}>
      {children}

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="private-password-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="private-password-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
          >
            <motion.form
              className="private-password-modal"
              onSubmit={submitPassword}
              initial={{ opacity: 0, y: 24, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            >
              <button
                type="button"
                className="private-password-close"
                aria-label="Close password dialog"
                onClick={closeModal}
              >
                <X size={21} aria-hidden="true" />
              </button>

              <div className="private-password-icon">
                <LockKeyhole size={30} aria-hidden="true" />
              </div>

              <span className="eyebrow">Private Memory</span>
              <h2 id="private-password-title">Enter the password</h2>
              <p>
                This photo or letter is private. Enter the password to unlock
                the personal memories for this browser session.
              </p>

              <label htmlFor="private-memory-password">Password</label>
              <input
                ref={inputRef}
                id="private-memory-password"
                type="password"
                value={password}
                autoComplete="current-password"
                inputMode="text"
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter password"
                aria-invalid={Boolean(error)}
              />

              {error && (
                <div className="private-password-error" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="primary-button private-password-submit"
                disabled={submitting || !password.trim()}
              >
                {submitting ? 'Checking...' : 'Unlock private memories'}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </PrivateContentContext.Provider>
  );
}

export function usePrivateContent() {
  const context = useContext(PrivateContentContext);

  if (!context) {
    throw new Error(
      'usePrivateContent must be used inside PrivateContentProvider',
    );
  }

  return context;
}